import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpHeaders, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {OidcService} from './oidc.service';
import {IApiErrorResponse} from "../interfaces/error/IApiErrorResponse";
import {AppConfigService} from "./app-config.service";
import {ErrorHandlingService} from "./error-handling.service";
import {EventSourceType} from "../enum/EventSourceType";
import {Router} from "@angular/router";
import {MatDialog} from "@angular/material/dialog";

@Injectable({
  providedIn: 'root'
})
export class APIService {
  private static readonly SIGNED_IN_AGAIN_AT_KEY = 'signed-in-again-after-401-at';

  // The rest of a batch of requests refused together must not act while the first one is redirecting to sign in.
  private signingInAgain = false;

  constructor(private oidcService: OidcService,
              private appConfigService: AppConfigService,
              private errorHandlingService: ErrorHandlingService,
              private router: Router,
              private matDialog: MatDialog,
              private http: HttpClient) {
  }

  public MakeGetRequest<T>(apiMethod: string, paramsP?: HttpParams): Observable<T> {
    const baseUrl = this.appConfigService.commonApiUrl();
    this.errorHandlingService.unauthorizedErrorMessageBehaviorSubject.next('');
    this.errorHandlingService.clearErrorMessages();

    return this.http.get<T>(`${baseUrl}/${apiMethod}`, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.oidcService.getAccessToken()}`,
        Accept: 'application/json'
      }),
      params: paramsP
    }).pipe(catchError((errorResponseP: HttpErrorResponse) => {
      this.handleAuthorizationFailure(errorResponseP);

      throw <IApiErrorResponse>{
        Error: errorResponseP.error,
        Method: apiMethod,
        RequestUrl: apiMethod,
        Status: errorResponseP.status
      };
    }));
  }

  public MakeDeleteRequest<T>(apiMethod: string, bodyP: any) {
    return this.MakeAPIRequest<T>('delete', apiMethod, bodyP);
  }

  public MakePostRequest<T>(apiMethod: string, bodyP: any, isFormDataP: boolean = false): Observable<T> {
    return this.MakeAPIRequest<T>('post', apiMethod, bodyP, isFormDataP);
  }

  // A background save must not wipe a message the admin is reading.
  public MakePutRequest<T>(apiMethod: string, bodyP: any, clearErrorMessagesP: boolean = true) {
    return this.MakeAPIRequest<T>('put', apiMethod, bodyP, false, clearErrorMessagesP);
  }

  private GetHttpOptions(paramsP?: any, isFormData: boolean = false) {
    if (paramsP) {
      paramsP.AuditEventSourceTypeId = EventSourceType.AdminApp;
    }

    return {
      body: paramsP ? JSON.stringify(paramsP) : undefined,
      headers: new HttpHeaders({
        'Content-Type': !isFormData ? 'application/json' : 'multipart/form-data',
        Authorization: `Bearer ${this.oidcService.getAccessToken()}`,
        Accept: 'application/json'
      })
    };
  }

  public uploadFile(apiMethodP: string, formData: FormData) {
    const baseUrl = this.appConfigService.commonApiUrl();

    return this.http.post(`${baseUrl}/${apiMethodP}`, formData, {
      headers: new HttpHeaders({
        Authorization: `Bearer ${this.oidcService.getAccessToken()}`
      }),
      reportProgress: true,
      observe: 'events'
    })
  }

  private MakeAPIRequest<T>(httpMethodP: string, apiMethodP: string, bodyP?: {}, isFormData: boolean = false,
                            clearErrorMessagesP: boolean = true): Observable<T> {
    if (clearErrorMessagesP) {
      this.errorHandlingService.clearErrorMessages();
    }
    const baseUrl = this.appConfigService.commonApiUrl();
    const options = this.GetHttpOptions(bodyP, isFormData);
    return this.http.request<T>(httpMethodP, `${baseUrl}/${apiMethodP}`, options).pipe(catchError((errorResponseP: HttpErrorResponse) => {
      this.handleAuthorizationFailure(errorResponseP);

      throw <IApiErrorResponse>{
        Error: errorResponseP.error,
        Method: apiMethodP,
        RequestUrl: apiMethodP,
        Status: errorResponseP.status
      };
    }));
  }

  private handleAuthorizationFailure(errorResponseP: HttpErrorResponse) {
    const session_ended = errorResponseP?.status === 401 && this.sessionHasEnded(errorResponseP);

    if ((!session_ended && errorResponseP?.status !== 403) || this.signingInAgain) {
      return;
    }

    // An open dialog would otherwise sit over the page this sends the admin to, showing a generic error instead.
    this.matDialog.closeAll();

    if (session_ended && !this.signedInAgainRecently()) {
      this.signingInAgain = true;
      sessionStorage.setItem(APIService.SIGNED_IN_AGAIN_AT_KEY, String(Date.now()));
      this.oidcService.signInAgain();
      return;
    }

    if (session_ended) {
      this.errorHandlingService.display401ErrorMessage();
    } else {
      this.errorHandlingService.display403ErrorMessage();
    }

    void this.router.navigateByUrl('not-authorized');
  }

  // Only the token check's own challenge means the session is over; the expiry check covers a proxy stripping it.
  private sessionHasEnded(errorResponseP: HttpErrorResponse): boolean {
    const challenge = errorResponseP.headers?.get('WWW-Authenticate') ?? '';
    return challenge.startsWith('Bearer') || !this.oidcService.isAuthenticated();
  }

  // A second refusal within a minute of signing in again would loop, so it goes to not-authorized instead.
  private signedInAgainRecently(): boolean {
    const signed_in_again_at = Number(sessionStorage.getItem(APIService.SIGNED_IN_AGAIN_AT_KEY));
    return Date.now() - signed_in_again_at < 60_000;
  }
}
